/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import React from 'react';
import { useAuthenticationTranslation } from '../../utils/i18n';
import type { Role } from '../ApiContext/types';

export const UserMenuRoles: React.FC<{
  currentRole?: Role;
  onChangeRole?: (role: Role) => void;
  roles?: Role[];
}> = ({ currentRole, onChangeRole, roles }) => {
  const { t } = useAuthenticationTranslation();

  const handleChangeRole = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const selectedRole = roles?.find(
      (role) => role.name === event.target.value,
    );
    if (selectedRole) {
      onChangeRole?.(selectedRole);
    }
  };

  return (
    <FormControl>
      <FormLabel
        component="legend"
        sx={{
          fontSize: 14,
          fontWeight: 400,
          color: 'geowebColors.typographyAndIcons.text',
          opacity: 1,
        }}
      >
        {t('auth-role-title')}
      </FormLabel>
      <RadioGroup
        aria-labelledby="user-menu-roles"
        name="user-menu-roles"
        onChange={handleChangeRole}
        value={currentRole?.name}
      >
        {roles?.map((role) => (
          <FormControlLabel
            control={<Radio />}
            key={role.name}
            label={t(role.title)}
            value={role.name}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};
